import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ErrorBar,
} from "recharts";

/**
 * Lumiplot Landing Chat Demo (Animated with Demo Cursor)
 * v1.0.21 – fix JSX near Modify buttons (wrap text in <span>), ensure no stray characters in JSX.
 *
 * Flow (hands-free for landing pages):
 * 1) Cursor focuses composer, types a light prompt.
 * 2) Prompt is sent → sticky pill shows "Creating recipes…" at the bottom.
 * 3) Plan card appears (Options 2 & 3 folded by default).
 * 4) Cursor expands Option 2, then Option 3, then clicks "Approve & run" (Option 1).
 * 5) Pill switches to staged labels → Collecting ingredients… → Cooking… → Tasting…; card stays; final grouped-bars plot appears.
 */

// ---------- Types ----------
export type PlanCardHandle = {
  getOpt2Btn: () => HTMLButtonElement | null;
  getOpt3Btn: () => HTMLButtonElement | null;
  getApproveBtn: () => HTMLButtonElement | null;
  toggleOption2: () => void;
  toggleOption3: () => void;
  closeOptions: () => void;
  scrollOption1IntoView: () => void;
  flashApprovePress: () => void;
};

// ---------- Helpers ----------
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={["transition-transform", open ? "rotate-180" : "rotate-0"].join(" ")}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default function LandingChatDemo({
  width = 520,
  height = 560,
  centered = false,
  autoCursor = true,
  planDelayMs = 2400,
}: {
  width?: number;
  height?: number;
  centered?: boolean;
  autoCursor?: boolean;
  planDelayMs?: number;
}) {
  const [prompt, setPrompt] = React.useState("");
  const [messages, setMessages] = React.useState<Array<{ id: string; role: "user" | "system"; text: string }>>([]);
  const [phase, setPhase] = React.useState<"idle" | "sending" | "planning" | "planned" | "running">("idle");
  // Running step sequence: 0=none, 1=Collecting, 2=Cooking, 3=Tasting, 4=Plot Ready
  const [runStep, setRunStep] = React.useState<0 | 1 | 2 | 3 | 4>(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [cursorInitialPos, setCursorInitialPos] = React.useState<{ x: number; y: number } | null>(null);

  // Refs for cursor targeting & scroll management
  const containerRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const sendRef = React.useRef<HTMLButtonElement>(null);
  const planCardRef = React.useRef<PlanCardHandle>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const plotRef = React.useRef<HTMLDivElement>(null);

  // Send button press feedback
  const [sendPressed, setSendPressed] = React.useState(false);

  // Cursor state
  const [cursor, setCursor] = React.useState<{ x: number; y: number; visible: boolean; click: boolean }>(
    {
      x: 24,
      y: 24,
      visible: false,
      click: false,
    }
  );

  // Smooth move & ensure visibility
  const moveCursorTo = React.useCallback(
    async (el: HTMLElement | null, dx = 0, dy = 0, dwell = 520) => {
      if (!containerRef.current || !el) return;

      // Ensure the target is visible inside the scroll area
      const scrollEl = scrollRef.current;
      let scrolled = false;
      if (scrollEl) {
        const er = el.getBoundingClientRect();
        const sr = scrollEl.getBoundingClientRect();
        if (er.bottom > sr.bottom - 48) {
          scrollEl.scrollTo({ top: scrollEl.scrollTop + (er.bottom - sr.bottom) + 120, behavior: "smooth" });
          scrolled = true;
        } else if (er.top < sr.top + 48) {
          scrollEl.scrollTo({ top: scrollEl.scrollTop - (sr.top - er.top) + 120, behavior: "smooth" });
          scrolled = true;
        }
      } else {
        // Fallback: use container-scoped scroll instead of scrollIntoView to prevent page scroll
        const container = containerRef.current;
        if (container) {
          const er = el.getBoundingClientRect();
          const cr = container.getBoundingClientRect();
          const scrollParent = container.closest('[style*="overflow"], [class*="overflow"]') as HTMLElement;
          if (scrollParent && (scrollParent.scrollHeight > scrollParent.clientHeight)) {
            const targetTop = er.top - cr.top + scrollParent.scrollTop - 48;
            scrollParent.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
            scrolled = true;
          }
        }
      }
      // Wait longer for smooth scroll to complete before measuring
      if (scrolled) {
        await sleep(400); // Increased from 160ms to allow smooth scroll to complete
      } else {
      await sleep(160);
      }

      // Re-measure after scroll settles - get fresh bounding rects
      // Use requestAnimationFrame to ensure DOM has updated after scroll
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      // Helper to calculate cursor position
      const calculatePosition = () => {
        const c = containerRef.current;
        const r = el;
        if (!c || !r) return null;
        
      const cRect = c.getBoundingClientRect();
      const rRect = r.getBoundingClientRect();
      const TIP_X = 18, TIP_Y = 13; // tip offset inside DemoCursor svg
      
      // Calculate position relative to container (which has position: relative)
      const targetX = rRect.left - cRect.left + rRect.width / 2 + dx;
      const targetY = rRect.top - cRect.top + rRect.height / 2 + dy;
      const x = targetX - TIP_X;
      const y = targetY - TIP_Y - 6; // Move slightly higher for better click accuracy
        
        return { x, y };
      };
      
      // Calculate and set position
      const pos = calculatePosition();
      if (pos) {
        setCursor((s) => ({ ...s, visible: true, x: pos.x, y: pos.y }));
        // Recalculate position after a short delay to account for any layout shifts
        await sleep(100);
        await new Promise(resolve => requestAnimationFrame(resolve));
        const pos2 = calculatePosition();
        if (pos2) {
          setCursor((s) => ({ ...s, visible: true, x: pos2.x, y: pos2.y }));
        }
      }
      
      await sleep(dwell);
    },
    []
  );

  // Instantly position the cursor at an element without travel animation (used at demo start)
  const positionCursorAt = React.useCallback(
    (el: HTMLElement | null, dx = 0, dy = 0, show = true) => {
      if (!containerRef.current || !el) return;
      const c = containerRef.current;
      const r = el;
      const cRect = c.getBoundingClientRect();
      const rRect = r.getBoundingClientRect();
      const TIP_X = 18, TIP_Y = 13;
      // Calculate position relative to container (which has position: relative)
      // Use getBoundingClientRect() which gives accurate viewport coordinates
      const targetX = rRect.left - cRect.left + rRect.width / 2 + dx;
      const targetY = rRect.top - cRect.top + rRect.height / 2 + dy;
      const x = targetX - TIP_X;
      const y = targetY - TIP_Y - 6; // Move slightly higher for better click accuracy
      setCursor((s) => ({ ...s, x, y, visible: show ? true : s.visible }));
    },
    []
  );

  const clickCursor = React.useCallback(async () => {
    setCursor((s) => ({ ...s, click: true }));
    await sleep(140);
    setCursor((s) => ({ ...s, click: false }));
  }, []);

  // --- Scroll detection: Start animation 1.2s after section is visible ---
  React.useEffect(() => {
    if (!containerRef.current || !autoCursor) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimating) {
            // Section is visible, start animation after 1.2s delay
            setTimeout(() => {
              // Calculate and store initial cursor position at textarea
              if (textareaRef.current && containerRef.current) {
                const c = containerRef.current;
                const r = textareaRef.current;
                const cRect = c.getBoundingClientRect();
                const rRect = r.getBoundingClientRect();
                const TIP_X = 18, TIP_Y = 13;
                const targetX = rRect.left - cRect.left + rRect.width / 2 - 20;
                const targetY = rRect.top - cRect.top + rRect.height / 2 - 6;
                const x = targetX - TIP_X;
                const y = targetY - TIP_Y - 6;
                setCursorInitialPos({ x, y });
                // Position cursor at textarea (invisible)
                positionCursorAt(textareaRef.current, -20, -6, false);
              }
              // Now set isAnimating to true - cursor is already positioned
              setIsAnimating(true);
            }, 1200);
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of the component is visible
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isAnimating, autoCursor, positionCursorAt]);

  // Hands-free demo sequence (single-run). All awaits are inside an async IIFE.
  const startedRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoCursor || !isAnimating || startedRef.current) return;
    startedRef.current = true;

    const type = async (text: string) => {
      for (const ch of text) {
        setPrompt((p) => p + ch);
        await sleep(15 + Math.random() * 20); // faster typing
      }
    };

    (async () => {
      await sleep(250);
      // Cursor position is already set and initialPos is stored
      // Just make it visible - framer-motion will use initialPos as starting point
      if (cursorInitialPos) {
        setCursor({ x: cursorInitialPos.x, y: cursorInitialPos.y, visible: true, click: false });
      } else {
        // Fallback: position and make visible
        if (textareaRef.current && containerRef.current) {
          positionCursorAt(textareaRef.current, -20, -6, true);
        }
      }
      await sleep(300);
      textareaRef.current?.focus();
      await type("Compare CO2 emissions by sector across countries. Use grouped bars with error bars.");
      await sleep(420);
      await moveCursorTo(sendRef.current, -2, -6, 200);
        // Recalculate position right before clicking
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (containerRef.current && sendRef.current) {
          const cRect = containerRef.current.getBoundingClientRect();
          const rRect = sendRef.current.getBoundingClientRect();
          const TIP_X = 18, TIP_Y = 13;
          const targetX = rRect.left - cRect.left + rRect.width / 2 - 2;
          const targetY = rRect.top - cRect.top + rRect.height / 2 - 6;
          const x = targetX - TIP_X;
          const y = targetY - TIP_Y - 3; // Move slightly higher for better click accuracy
          setCursor((s) => ({ ...s, visible: true, x, y }));
        }
      await sleep(100);
      await clickCursor();
      setSendPressed(true);
      await sleep(160);
      setSendPressed(false);

      // send
      const content = (textareaRef.current?.value || "").trim();
      if (content) {
        const id = Math.random().toString(36).slice(2);
        setMessages((m) => [...m, { id, role: "user", text: content }]);
        setPrompt("");
        setPhase("sending");
        setTimeout(() => setPhase("planning"), 150);
        setTimeout(() => setPhase("planned"), planDelayMs);
        // keep the viewport anchored near the bottom while waiting
        setTimeout(() => {
          const s = scrollRef.current;
          if (s) s.scrollTo({ top: s.scrollHeight, behavior: "smooth" });
        }, 10);
      }

      // keep cursor parked on the Send button (no drift)
      await sleep(300);
      textareaRef.current?.blur();
      // Re-anchor the cursor to the send button in case layout shifted
      positionCursorAt(sendRef.current, -2, -6, true);
    })();
  }, [autoCursor, isAnimating, planDelayMs, moveCursorTo, clickCursor, positionCursorAt]);

  // After plan appears: expand Option 2, expand Option 3, then click Approve & run
  React.useEffect(() => {
    if (phase !== "planned") return;
    (async () => {
      await sleep(900);
      const o2 = planCardRef.current?.getOpt2Btn();
      if (o2) {
        await moveCursorTo(o2, 2, 0, 400);
        // Recalculate position right before clicking
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (containerRef.current && o2) {
          const cRect = containerRef.current.getBoundingClientRect();
          const rRect = o2.getBoundingClientRect();
          const TIP_X = 18, TIP_Y = 13;
          const targetX = rRect.left - cRect.left + rRect.width / 2 + 2;
          const targetY = rRect.top - cRect.top + rRect.height / 2;
          const x = targetX - TIP_X;
          const y = targetY - TIP_Y - 3; // Move slightly higher for better click accuracy
          setCursor((s) => ({ ...s, visible: true, x, y }));
        }
        await sleep(100);
        await clickCursor();
        await sleep(220);
        planCardRef.current?.toggleOption2();
      }

      await sleep(1000);
      const o3 = planCardRef.current?.getOpt3Btn();
      if (o3) {
        await moveCursorTo(o3, 2, 0, 400);
        // Recalculate position right before clicking
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (containerRef.current && o3) {
          const cRect = containerRef.current.getBoundingClientRect();
          const rRect = o3.getBoundingClientRect();
          const TIP_X = 18, TIP_Y = 13;
          const targetX = rRect.left - cRect.left + rRect.width / 2 + 2;
          const targetY = rRect.top - cRect.top + rRect.height / 2;
          const x = targetX - TIP_X;
          const y = targetY - TIP_Y - 3; // Move slightly higher for better click accuracy
          setCursor((s) => ({ ...s, visible: true, x, y }));
        }
        await sleep(100);
        await clickCursor();
        await sleep(220);
        planCardRef.current?.toggleOption3();
        // smooth: PlanCard handles its own scroll for open3
      }

      // After showing Option 3, scroll back up so Option 1 is visible
      await sleep(600);
      planCardRef.current?.scrollOption1IntoView();
      
      // Wait for scroll animation to fully complete - use a more reliable method
      const scrollEl = scrollRef.current;
      if (scrollEl) {
        // Wait for smooth scroll animation to complete (typically 300-500ms)
        await sleep(600);
        // Wait for any pending scroll animations to finish - check for stability
        await new Promise(resolve => {
          let lastScrollTop = scrollEl.scrollTop;
          let stableCount = 0;
          const checkStable = () => {
            requestAnimationFrame(() => {
              const currentScrollTop = scrollEl.scrollTop;
              if (Math.abs(currentScrollTop - lastScrollTop) < 0.5) {
                stableCount++;
                if (stableCount >= 5) {
                  // Scroll has been stable for 5 frames - fully complete
                  resolve(undefined);
                } else {
                  lastScrollTop = currentScrollTop;
                  checkStable();
                }
              } else {
                stableCount = 0;
                lastScrollTop = currentScrollTop;
                checkStable();
              }
            });
          };
          checkStable();
        });
        // Additional buffer to ensure scroll is truly done
        await sleep(200);
      } else {
        await sleep(800);
      }
      
      const approve = planCardRef.current?.getApproveBtn();
      if (approve) {
        // Wait for DOM to settle after scroll
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        
        // Helper function to calculate and set cursor position
        const positionCursor = () => {
          if (!containerRef.current || !approve) return;
          const cRect = containerRef.current.getBoundingClientRect();
          const rRect = approve.getBoundingClientRect();
          const TIP_X = 18, TIP_Y = 13;
          // Calculate center of button relative to container
          const targetX = rRect.left - cRect.left + rRect.width / 2;
          const targetY = rRect.top - cRect.top + rRect.height / 2;
          // Position cursor so tip is at button center, moved slightly higher for accuracy
          const x = targetX - TIP_X;
          const y = targetY - TIP_Y - 3;
          setCursor((s) => ({ ...s, visible: true, x, y }));
        };
        
        // Position cursor initially
        positionCursor();
        
        // Wait for cursor to be positioned and visible
        await sleep(400);
        
        // Recalculate position right before clicking to account for any layout shifts
        await new Promise(resolve => requestAnimationFrame(resolve));
        positionCursor();
        await sleep(100);
        
        // Final position check right before click
        await new Promise(resolve => requestAnimationFrame(resolve));
        positionCursor();
        await sleep(50);
        
        // Now click - cursor position has been verified multiple times
        await clickCursor();
        planCardRef.current?.flashApprovePress?.();
        await sleep(140);
        planCardRef.current?.closeOptions();
        setPhase("running");
        setRunStep(1);
        const s = scrollRef.current;
        if (s) s.scrollTo({ top: s.scrollHeight, behavior: "smooth" });
        await sleep(1200);
        setCursor((st) => ({ ...st, visible: false }));
      }
    })();
  }, [phase, moveCursorTo, clickCursor]);

  // --- Grouped bars data (from Plot Editor) ---
  const countries = ["Demeria", "Borealis Union", "Elysia", "Auroria", "Celestria"];
  const power = [660.0, 990.0, 1650.0, 2420.0, 4400.0];
  const transport = [495.0, 715.0, 660.0, 1870.0, 1650.0];
  const industry = [418.0, 605.0, 990.0, 1320.0, 2420.0];
  const rows = countries.map((c, i) => ({
    country: c,
    power: power[i],
    transport: transport[i],
    industry: industry[i],
    powerErr: Math.max(10, Math.round(power[i] * 0.07)),
    transportErr: Math.max(10, Math.round(transport[i] * 0.07)),
    industryErr: Math.max(10, Math.round(industry[i] * 0.07)),
    total: power[i] + transport[i] + industry[i],
  }));
  const yMax = 5000;
  const colors = { industry: "#DDEAF5", power: "#5CA3D0", transport: "#14549F" } as const;

  // When phase enters running, advance through steps → show plot
  React.useEffect(() => {
    if (phase !== "running") return;
    // schedule sequenced steps; avoid depending on runStep so later timers aren't cleared on re-render
    const t1 = setTimeout(() => setRunStep(2), 1200); // Cooking
    const t2 = setTimeout(() => setRunStep(3), 2400); // Tasting
    const t3 = setTimeout(() => setRunStep(4), 3600); // Plot ready
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [phase]);

  // When the plot is ready, scroll to the bottom of the plot/card
  React.useEffect(() => {
    if (phase === "running" && runStep >= 4) {
      requestAnimationFrame(() => {
        // Use container-scoped scroll instead of scrollIntoView to prevent page scroll
        const s = scrollRef.current;
        const plot = plotRef.current;
        if (s && plot) {
          // Calculate scroll position to show the plot element
          const plotRect = plot.getBoundingClientRect();
          const scrollRect = s.getBoundingClientRect();
          const targetScrollTop = s.scrollTop + (plotRect.top - scrollRect.top) - 48;
          s.scrollTo({ top: Math.max(0, Math.min(targetScrollTop, s.scrollHeight)), behavior: "smooth" });
          // Also scroll to bottom to ensure plot is fully visible
          setTimeout(() => {
            s.scrollTo({ top: s.scrollHeight, behavior: "smooth" });
          }, 100);
        } else if (s) {
          s.scrollTo({ top: s.scrollHeight, behavior: "smooth" });
        }
      });
    }
  }, [phase, runStep]);

  const runningLabel = runStep === 1 ? "Collecting ingredients…" : runStep === 2 ? "Cooking…" : runStep === 3 ? "Tasting…" : null;

  return (
    <motion.div
      ref={containerRef}
      aria-hidden="true"
      initial={{ opacity: 0, scale: 0.96, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={[
        "group relative select-none overflow-hidden rounded-xl",
        "shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]",
        "border border-black/10 bg-white/95 backdrop-blur",
        centered ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "mx-auto my-6",
      ].join(" ")}
      style={{ width, height, zIndex: 10 }}
    >
      {/* Title bar */}
      <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
          Lumi
        </div>
        <div className="-mr-1 flex items-center">
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
            disabled
          >
            Open app
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex h-[calc(100%-28px)] flex-col bg-neutral-50/60">
        {/* Scroll area */}
        <div 
          ref={scrollRef} 
          className="flex-1 overflow-auto overscroll-contain p-4"
          style={{ overscrollBehaviorY: 'contain', overscrollBehaviorX: 'contain', scrollbarGutter: 'stable both-edges' }}
        >
          <div className="mx-auto max-w-[560px] space-y-3">
            {/* Soft system notice */}
            <div className="pointer-events-none inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white/90 px-3 py-2 text-[11px] text-neutral-600 shadow-sm">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Upload complete: <span className="font-mono">sample.csv</span> 24 columns · 3,412 rows
            </div>

            {/* User bubbles */}
            <AnimatePresence initial={false}>
              {messages.map((m) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                  className="flex justify-end"
                >
                  <div className="max-w-[82%] rounded-lg bg-neutral-100 px-3 py-2 text-[13px] leading-snug text-neutral-800">
                    {m.text}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Visualization plans header + Plan Card */}
            <AnimatePresence>
              {(phase === "planned" || phase === "running") && (
                <motion.div
                  key="plan"
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="text-[12px] text-neutral-600 mb-2">Visualization plans</div>
                  <PlanCard ref={planCardRef} isRunning={phase === "running"} />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Final plot (from Plot Editor) */}
            {phase === "running" && runStep >= 4 && (
              <motion.div
                ref={plotRef}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="mt-3 rounded-lg border border-black/10 bg-white p-2"
              >
                {/* Header (title + stats + legend row) */}
                <div className="mb-2">
                  <div className="text-[13px] font-semibold text-neutral-900 mt-1">CO2 Emissions by Sector and Country</div>
                  <div className="mt-1 flex items-center justify-between">
                    <div className="text-[12px] text-neutral-600">ANOVA F=1.282, p = 0.313</div>
                    <div className="flex items-center gap-4 text-[12px] text-neutral-600">
                      <div className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colors.industry }} /> Industry</div>
                      <div className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colors.power }} /> Power</div>
                      <div className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colors.transport }} /> Transport</div>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rows} margin={{ left: 48, right: 16, top: 28, bottom: 28 }} barCategoryGap="28%" barGap={6}>
                      <CartesianGrid vertical={false} horizontal stroke="rgba(0,0,0,0.08)" />
                      <XAxis
                        type="category"
                        dataKey="country"
                        interval={0}
                        angle={-18}
                        textAnchor="end"
                        height={28}
                        tickMargin={0}
                        tick={{ fontSize: 12, fill: "#111", dy: 0 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="number"
                        tickFormatter={(v) => Number(v).toLocaleString()}
                        tick={{ fontSize: 12, fill: "#111" }}
                        axisLine={false}
                        tickLine={false}
                        tickCount={6}
                        domain={[0, yMax]}
                        width={48}
                        tickMargin={6}
                        label={{ value: "CO2 Emissions (Mt CO2)", angle: -90, position: "left", offset: 8, style: { fill: "#111", fontSize: 12, textAnchor: "middle" } }}
                      />

                      <Bar dataKey="industry" name="Industry" fill={colors.industry}>
                        <ErrorBar dataKey="industryErr" width={4} stroke={colors.industry} strokeWidth={1.25} direction="y" />
                      </Bar>
                      <Bar dataKey="power" name="Power" fill={colors.power}>
                        <ErrorBar dataKey="powerErr" width={4} stroke={colors.power} strokeWidth={1.25} direction="y" />
                      </Bar>
                      <Bar dataKey="transport" name="Transport" fill={colors.transport}>
                        <ErrorBar dataKey="transportErr" width={4} stroke={colors.transport} strokeWidth={1.25} direction="y" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            )}

            {/* Bottom sticky progress indicator to mimic app */}
            {(phase === "sending" || phase === "planning" || (phase === "running" && runningLabel)) && (
              <div className="sticky bottom-0 z-10 mt-1">
                <div
                  className="flex items-center gap-2 rounded-full bg-white/80 px-2.5 py-1 text-[11px] text-neutral-500 w-fit shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-black/10"
                  role="status"
                  aria-live="polite"
                >
                  <Spinner className="h-3 w-3" />
                  {phase === "running" ? (runningLabel ?? "") : "Creating recipes…"}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="border-t border-black/10 bg-white/80 p-3">
          <div className="mx-auto max-w-[560px]">
            <div className="relative flex items-start rounded-2xl border border-neutral-300 bg-white p-2 pr-12 pointer-events-none">
              <textarea
                ref={textareaRef}
                rows={1}
                value={prompt}
                readOnly
                tabIndex={-1}
                aria-readonly="true"
                placeholder="What would you like to visualize?"
                className="block w-full resize-none bg-transparent text-[13px] leading-6 text-neutral-700 placeholder:text-neutral-400 focus:outline-none caret-transparent"
                style={{ minHeight: 44 }}
              />
              <div className="absolute bottom-2 right-2 flex items-center gap-1">
                <button
                  ref={sendRef}
                  onClick={() => {}}
                  className={[
                    "pointer-events-none flex h-9 w-9 items-center justify-center rounded-full text-black transition-transform",
                    phase !== "idle" ? "bg-[#ffd500]/80" : "bg-[#ffd500]",
                    sendPressed ? "scale-[0.98] translate-y-px shadow-inner ring-1 ring-black/10" : "",
                  ].join(" ")}
                  aria-label="Send"
                  aria-pressed={sendPressed}
                  title="Send"
                >
                  <ArrowUpIcon className="h-[18px] w-[18px]" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Demo Cursor overlay */}
        {cursor.visible && cursorInitialPos && (
          <motion.div
            key="cursor"
            className="pointer-events-none absolute z-50"
            style={{ transformOrigin: "18px 13px" }}
            initial={{ x: cursorInitialPos.x, y: cursorInitialPos.y, scale: 1 }}
            animate={{ x: cursor.x, y: cursor.y, scale: cursor.click ? 0.86 : 1 }}
            transition={{
              x: { type: "tween", ease: "easeInOut", duration: 0.4 },
              y: { type: "tween", ease: "easeInOut", duration: 0.4 },
              scale: { type: "tween", ease: "easeInOut", duration: 0.12 }
            }}
          >
            <DemoCursor clicking={cursor.click} />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

const PlanCard = React.forwardRef<PlanCardHandle, { isRunning?: boolean }>(function PlanCard({ isRunning = false }, ref) {
  const [open2, setOpen2] = React.useState(false);
  const [open3, setOpen3] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const opt2BtnRef = React.useRef<HTMLButtonElement>(null);
  const opt3BtnRef = React.useRef<HTMLButtonElement>(null);
  const approveRef = React.useRef<HTMLButtonElement>(null);
  const opt3EndRef = React.useRef<HTMLDivElement>(null);
  const planCardContainerRef = React.useRef<HTMLDivElement>(null);

  // When Option 3 opens, make sure the bottom of its content is visible
  React.useEffect(() => {
    if (open3) {
      requestAnimationFrame(() => {
        // Use container-scoped scroll instead of scrollIntoView to prevent page scroll
        const scrollContainer = opt3EndRef.current?.closest('[class*="overflow-auto"]') as HTMLElement;
        const sentinel = opt3EndRef.current;
        if (scrollContainer && sentinel) {
          const sentinelRect = sentinel.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetScrollTop = scrollContainer.scrollTop + (sentinelRect.bottom - containerRect.bottom) + 48;
          scrollContainer.scrollTo({ top: Math.max(0, Math.min(targetScrollTop, scrollContainer.scrollHeight)), behavior: "smooth" });
        }
      });
    }
  }, [open3]);

  React.useImperativeHandle(ref, () => ({
    getOpt2Btn: () => opt2BtnRef.current,
    getOpt3Btn: () => opt3BtnRef.current,
    getApproveBtn: () => approveRef.current,
    toggleOption2: () => setOpen2((v) => !v),
    toggleOption3: () => setOpen3((v) => !v),
    closeOptions: () => {
      setOpen2(false);
      setOpen3(false);
    },
    scrollOption1IntoView: () => {
      // Scroll to show more of the recommended plan card above the approve button
      // Find the scroll container (parent scroll area)
      const scrollContainer = approveRef.current?.closest('[class*="overflow-auto"]') as HTMLElement;
      if (scrollContainer && approveRef.current) {
        const buttonRect = approveRef.current.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        // Calculate scroll position to show button in lower portion of viewport (about 60% down)
        // This shows more content above the button
        const targetButtonTop = containerRect.top + (containerRect.height * 0.6);
        const scrollOffset = buttonRect.top - targetButtonTop + scrollContainer.scrollTop;
        scrollContainer.scrollTo({ top: Math.max(0, Math.min(scrollOffset, scrollContainer.scrollHeight)), behavior: "smooth" });
      }
      // Removed fallback scrollIntoView to prevent page scroll
    },
    flashApprovePress: () => {
      setPressed(true);
      setTimeout(() => setPressed(false), 160);
    },
  }));

  return (
    <div className="w-full">
      <div className="space-y-2">
        {/* Primary option (Recommended) */}
        <div className="rounded-lg border border-yellow-400 bg-yellow-50/30 p-3">
          {/* Header row inside card: left label + right pill */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[13px] font-medium text-neutral-900">Recommended</div>
            <div className="rounded-full bg-yellow-500/20 px-1.5 py-0.5 text-[10px] font-medium text-yellow-700">Recommended</div>
          </div>

          {/* Title */}
          <div className="text-[14px] font-semibold text-neutral-900 mb-1.5">CO2 Emissions by Sector and Country</div>

          {/* Mappings */}
          <div className="space-y-0.5 text-[11px] text-neutral-600 mb-1.5">
            <div>Chart: grouped_bar</div>
            <div>X: country</div>
            <div>Y: emissions_mt_co2</div>
            <div>Group: sector</div>
            <div>Stats: ANOVA</div>
          </div>

          {/* Rationale */}
          <div className="text-[11px] leading-relaxed text-neutral-500 mb-2">
            Grouped bars place Industry, Power, and Transport side-by-side for each country so sector totals can be compared directly; symmetric error bars communicate uncertainty. Suitable for ANOVA across sectors and countries.
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              ref={approveRef}
              disabled={isRunning}
              aria-pressed={isRunning || pressed}
              className={[
                "rounded px-2.5 py-1 text-[12px] font-medium transition-transform",
                isRunning ? "bg-amber-300/70 text-black/80 cursor-default pointer-events-none flex items-center gap-1.5" : "text-black",
                !isRunning && pressed ? "scale-[0.98] translate-y-px shadow-inner ring-1 ring-black/10" : "",
              ].join(" ")}
              style={{ backgroundColor: isRunning ? undefined : "#ffd500" }}
            >
              <span>{"Approve & run"}</span>
            </button>
            <button className="rounded bg-neutral-100 px-2.5 py-1 text-[12px] font-medium text-neutral-700">
              <span>Modify</span>
            </button>
          </div>
        </div>

        {/* Collapsible Option 2 */}
        <div className="rounded-lg border border-neutral-200 bg-white">
          <button
            ref={opt2BtnRef}
            onClick={() => setOpen2((v) => !v)}
            className="flex w-full items-center justify-between p-2 text-left"
          >
            <span className="text-[12px] font-medium text-neutral-900">Option 2 · Stacked Bar — Sector Shares</span>
            <Chevron open={open2} />
          </button>
          {open2 && (
            <div className="border-t border-neutral-200 p-2">
              <div className="space-y-0.5 text-[11px] text-neutral-600 mb-1.5">
                <div>Chart: stacked_bar</div>
                <div>X: country</div>
                <div>Y: emission_share</div>
                <div>Group: sector</div>
                <div>Stats: NA</div>
              </div>
              <div className="text-[11px] leading-relaxed text-neutral-500 mb-2">
                100% stacked bars to show sector composition per country; makes relative shares comparable while preserving country ordering.
              </div>
              <div className="flex items-center gap-2">
                <button className="rounded bg-neutral-100 px-2.5 py-1 text-[12px] font-medium text-neutral-700"><span>Approve & Run</span></button>
                <button className="rounded bg-neutral-100 px-2.5 py-1 text-[12px] font-medium text-neutral-700"><span>Modify</span></button>
              </div>
            </div>
          )}
        </div>

        {/* Collapsible Option 3 */}
        <div className="rounded-lg border border-neutral-200 bg-white">
          <button
            ref={opt3BtnRef}
            onClick={() => setOpen3((v) => !v)}
            className="flex w-full items-center justify-between p-2 text-left"
          >
            <span className="text-[12px] font-medium text-neutral-900">Option 3 · Heatmap — Sector Share by Country</span>
            <Chevron open={open3} />
          </button>
          {open3 && (
            <div className="border-t border-neutral-200 p-2">
              <div className="space-y-0.5 text-[11px] text-neutral-600 mb-1.5">
                <div>Chart: heatmap</div>
                <div>X: sector</div>
                <div>Y: country</div>
                <div>Value: emission_share</div>
                <div>Stats: NA</div>
              </div>
              <div className="text-[11px] leading-relaxed text-neutral-500 mb-2">
                Heatmap where color encodes each sector's share of total emissions by country; highlights transport-heavy vs industry-heavy patterns.
              </div>
              <div className="flex items-center gap-2">
                <button className="rounded bg-neutral-100 px-2.5 py-1 text-[12px] font-medium text-neutral-700"><span>Approve & Run</span></button>
                <button className="rounded bg-neutral-100 px-2.5 py-1 text-[12px] font-medium text-neutral-700"><span>Modify</span></button>
              </div>
              {/* sentinel for scroll-into-view */}
              <div ref={opt3EndRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

function Spinner({ className = "" }: { className?: string }) {
  return (
    <svg
      className={["animate-spin", className].join(" ")}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".2" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function ArrowUpIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function DemoCursor({ clicking = false }: { clicking?: boolean }) {
  return (
    <div className="relative">
      {/* Arrow cursor */}
      <svg
        className="drop-shadow-md"
        width="22"
        height="26"
        viewBox="0 0 24 28"
        aria-hidden="true"
      >
        {/* classic arrow pointer with white outline */}
        <path
          d="M3 2 L3 22 L9 16 L12.5 24 L14.7 23 L11.3 15 L19.5 15 Z"
          fill="#111111"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
      {/* Click ripple at the arrow tip */}
      <div
        className={[
          "pointer-events-none absolute left-[18px] top-[13px] h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full",
          clicking ? "animate-[ping_0.6s_ease-out_1] bg-neutral-400/40" : "",
        ].join(" ")}
      />
    </div>
  );
}

// --- lightweight smoke tests to ensure exports are usable
export function __demo_selfTest() {
  return {
    hasSpinner: typeof Spinner === "function",
    hasPlanCard: true,
    handleShape: [
      "getOpt2Btn",
      "getOpt3Btn",
      "getApproveBtn",
      "toggleOption2",
      "toggleOption3",
      "closeOptions",
      "scrollOption1IntoView",
      "flashApprovePress",
    ],
    defaultPlanDelay: 2400,
  } as const;
}

// Additional test: button feedback copy when running (label stays constant)
export function __demo_buttonFeedbackTest() {
  return { runningCopyButton: "Approve & Run" } as const;
}

// Additional test: ensure phase contract contains running
export function __demo_phaseContractTest() {
  const phases = ["idle", "sending", "planning", "planned", "running"] as const;
  return { includesRunning: phases.includes("running") } as const;
}

// Additional test: progress copy
export function __demo_progressCopyTest() {
  return {
    planningCopy: "Creating recipes…",
    runningCopy: "Collecting ingredients…",
  } as const;
}

// Additional test: step labels order
export function __demo_stepLabelsTest() {
  return { steps: ["Collecting ingredients…", "Cooking…", "Tasting…"] } as const;
}
